/**
 * PendingListingsPanel — the gallery shown after Apify finishes scraping.
 *
 * Flow:
 *   1. GET /api/admin/prospecting/pending-listings → cards per pand
 *   2. Click a card → photo-grid modal
 *   3. Admin picks max 3 photos → "AI-suggestie" POSTs to /score-photos
 *   4. Admin tweaks action dropdowns → "Genereer draft" POSTs to /generate
 *   5. Draft appears in "Te reviewen" panel
 *
 * Keeps Fal.ai credits low: nothing happens until the admin commits.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ExternalLink, MapPin, Wand2, X, Check, Trash2, Sparkles, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const ACTION_OPTIONS = [
    { value: "sunny",      label: "☀️ Zonnig (lucht)" },
    { value: "declutter",  label: "🧹 Opruimen" },
    { value: "add_plants", label: "🌿 Planten erbij" },
    { value: "restyle",    label: "🛋️ Restyle interieur" },
    { value: "relight",    label: "✨ Lichter maken" },
    { value: "blur",       label: "👤 Privacy-blur" },
];

export function PendingListingsPanel({ onDraftCreated }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIdx, setOpenIdx] = useState(null); // index in items[] for modal
    // Standaard INGEKLAPT — de lijst kan 30+ panden bevatten en duwde de rest
    // van de pagina onbereikbaar ver naar beneden. Keuze onthouden.
    const [expanded, setExpanded] = useState(() => localStorage.getItem("vsf_pending_listings_open") === "1");
    const [showAll, setShowAll] = useState(false);
    const toggleExpanded = () => {
        setExpanded((v) => {
            localStorage.setItem("vsf_pending_listings_open", v ? "0" : "1");
            return !v;
        });
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/prospecting/pending-listings");
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon panden niet laden");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh: 5s while a card is generating so the user sees the
    // draft pop into "Te reviewen" the moment the background task finishes,
    // 15s otherwise to keep the dashboard quiet.
    useEffect(() => {
        const anyGenerating = items.some((i) => i.status === "generating");
        const interval = anyGenerating ? 5000 : 15000;
        const t = setInterval(() => { load(); }, interval);
        return () => clearInterval(t);
    }, [load, items]);

    const reject = async (item) => {
        if (!window.confirm(`Pand "${item.listing_title}" verwijderen uit selectie?`)) return;
        try {
            await api.delete(`/admin/prospecting/runs/${item.run_id}/listings/${item.idx}`);
            toast.success("Pand verwijderd");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon niet verwijderen");
        }
    };

    if (loading && items.length === 0) {
        return (
            <section className="bg-white border border-zinc-200 rounded-sm p-8 text-center" data-testid="pending-listings-loading">
                <Loader2 className="h-5 w-5 animate-spin text-zinc-500 mx-auto" />
            </section>
        );
    }

    if (items.length === 0) {
        return null; // hide entirely when nothing pending — keeps the dashboard clean
    }

    const openItem = openIdx !== null ? items[openIdx] : null;
    const generatingCount = items.filter((i) => i.status === "generating").length;
    const visibleItems = showAll ? items : items.slice(0, 6);

    return (
        <section className="bg-white border border-zinc-200 rounded-sm" data-testid="pending-listings-panel">
            <header
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none hover:bg-zinc-50 transition-colors"
                onClick={toggleExpanded}
                data-testid="pending-listings-toggle"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <Wand2 className="h-4 w-4 text-[#E5A93D] shrink-0" />
                    <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em] truncate">
                        Te selecteren panden
                    </h2>
                    <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-sm bg-[#E5A93D]/15 text-[#9A6E1B] text-xs font-bold" data-testid="pending-listings-count">
                        {items.length}
                    </span>
                    {generatingCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#9A6E1B] font-bold">
                            <Loader2 className="h-3 w-3 animate-spin" /> {generatingCount} bezig
                        </span>
                    )}
                    {!expanded && (
                        <span className="hidden sm:inline text-xs text-zinc-400 ml-1">— klik om de panden te tonen</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {expanded && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); load(); }}
                            data-testid="pending-listings-refresh"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-xs"
                        >
                            <RefreshCw className="h-3 w-3" /> Vernieuw
                        </button>
                    )}
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-sm border border-zinc-200 text-zinc-500">
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
                    </span>
                </div>
            </header>

            {expanded && (
                <div className="border-t border-zinc-200">
                    <p className="px-4 pt-3 text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                        Klik een pand → selecteer 3 foto&apos;s → kies de AI-bewerking per foto → genereer de draft.
                    </p>

                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {visibleItems.map((item) => (
                            <ListingCard
                                key={`${item.run_id}-${item.idx}`}
                                item={item}
                                onOpen={() => setOpenIdx(items.indexOf(item))}
                                onReject={() => reject(item)}
                            />
                        ))}
                    </div>
                    {items.length > 6 && (
                        <div className="px-4 pb-4 text-center">
                            <button
                                type="button"
                                onClick={() => setShowAll((v) => !v)}
                                data-testid="pending-listings-show-all"
                                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm border border-zinc-300 text-zinc-700 hover:bg-zinc-100 text-xs font-bold"
                            >
                                {showAll ? "Toon minder" : `Toon alle ${items.length} panden`}
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {openItem && (
                <PhotoPickerModal
                    item={openItem}
                    onClose={() => setOpenIdx(null)}
                    onDone={async (draftId) => {
                        toast.success("Draft aangemaakt — beschikbaar in 'Te reviewen'");
                        setOpenIdx(null);
                        await load();
                        if (onDraftCreated) onDraftCreated(draftId);
                    }}
                />
            )}
        </section>
    );
}

export default PendingListingsPanel;


function ListingCard({ item, onOpen, onReject }) {
    const cover = (item.photo_urls || [])[0];
    const count = (item.photo_urls || []).length;
    const isGenerating = item.status === "generating";
    const errorMsg = item.last_error;
    return (
        <article
            className={`group border rounded-sm overflow-hidden bg-zinc-50 transition ${
                isGenerating ? "border-[#E5A93D]/60 ring-1 ring-[#E5A93D]/30" : "border-zinc-200 hover:border-[#E5A93D]/40 cursor-pointer"
            }`}
            onClick={isGenerating ? undefined : onOpen}
            data-testid={`pending-listing-card-${item.idx}`}
            data-status={item.status}
        >
            <div className="aspect-[4/3] bg-zinc-50 relative">
                {cover && (
                    <img src={cover} alt={item.listing_title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                )}
                {isGenerating && (
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm grid place-items-center text-center px-4">
                        <div>
                            <Loader2 className="h-8 w-8 animate-spin text-[#E5A93D] mx-auto mb-2" />
                            <p className="text-xs font-bold text-[#E5A93D] uppercase tracking-[0.14em]">AI aan het werk</p>
                            <p className="text-[11px] text-zinc-300 mt-1">3× Fal.ai retouch (~30 sec)</p>
                        </div>
                    </div>
                )}
                <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-black/70 text-white text-[10px] uppercase tracking-[0.1em] px-2 h-6 rounded-sm font-bold">
                    {count} foto&apos;s
                </div>
                {!isGenerating && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onReject(); }}
                        data-testid={`pending-listing-reject-${item.idx}`}
                        title="Pand verwijderen uit selectie"
                        className="absolute top-2 left-2 h-7 w-7 grid place-items-center rounded-sm bg-black/60 hover:bg-red-500/70 text-zinc-300 hover:text-white opacity-0 group-hover:opacity-100 transition"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            <div className="p-3">
                <h3 className="font-display text-sm font-bold leading-tight truncate" data-testid={`pending-listing-title-${item.idx}`}>
                    {item.agency_name || "Onbekend kantoor"}
                </h3>
                <div className="mt-1 text-xs text-zinc-500 truncate">
                    {item.listing_title}
                </div>
                {item.city && (
                    <div className="mt-0.5 text-[11px] text-zinc-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {item.city}
                    </div>
                )}
                {errorMsg && (
                    <div className="mt-2 px-2 py-1.5 rounded-sm bg-red-500/10 border border-red-500/30 text-[10px] text-red-700" data-testid={`pending-listing-error-${item.idx}`}>
                        ⚠ Vorige poging mislukt: {errorMsg.slice(0, 80)}
                    </div>
                )}
                <div className="mt-3 flex items-center gap-2">
                    <button
                        type="button"
                        disabled={isGenerating}
                        data-testid={`pending-listing-open-${item.idx}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isGenerating ? (
                            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Bezig…</>
                        ) : (
                            <><Wand2 className="h-3.5 w-3.5" /> {errorMsg ? "Opnieuw proberen" : "Kies foto's"}</>
                        )}
                    </button>
                    {item.listing_url && (
                        <a
                            href={item.listing_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`pending-listing-external-${item.idx}`}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-sm border border-zinc-200 text-zinc-500 hover:text-zinc-800"
                            title="Open op Immoweb"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                    )}
                </div>
            </div>
        </article>
    );
}


function PhotoPickerModal({ item, onClose, onDone }) {
    const photos = item.photo_urls || [];
    const [selected, setSelected] = useState([]); // ordered list of photo_indexes (max 3)
    const [actions, setActions] = useState({});   // {photo_idx: action_value}
    const [notes, setNotes] = useState({});       // {photo_idx: "extra opmerking"}
    const [showNoteFor, setShowNoteFor] = useState({});  // {photo_idx: bool}
    const [scoring, setScoring] = useState(false);
    const [generating, setGenerating] = useState(false);

    // Lock body scroll
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    const togglePhoto = (idx) => {
        setSelected((s) => {
            if (s.includes(idx)) {
                const next = s.filter((x) => x !== idx);
                setActions((a) => { const c = { ...a }; delete c[idx]; return c; });
                setNotes((n) => { const c = { ...n }; delete c[idx]; return c; });
                setShowNoteFor((nf) => { const c = { ...nf }; delete c[idx]; return c; });
                return next;
            }
            if (s.length >= 3) {
                toast.error("Max 3 foto's per draft");
                return s;
            }
            setActions((a) => ({ ...a, [idx]: a[idx] || "relight" }));
            return [...s, idx];
        });
    };

    const runVision = async () => {
        if (selected.length === 0) {
            toast.error("Selecteer eerst foto's");
            return;
        }
        setScoring(true);
        try {
            const { data } = await api.post(
                `/admin/prospecting/runs/${item.run_id}/listings/${item.idx}/score-photos`,
                { photo_indexes: selected }
            );
            const scores = data.photo_scores || {};
            // Apply suggested actions
            setActions((a) => {
                const next = { ...a };
                selected.forEach((i) => {
                    const s = scores[String(i)];
                    if (s && s.best_action) next[i] = s.best_action;
                });
                return next;
            });
            toast.success(`Vision-suggesties klaar voor ${selected.length} foto(${selected.length === 1 ? "" : "'s"})`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vision-scoring faalde");
        } finally {
            setScoring(false);
        }
    };

    // No distinct-action requirement — admin can pick 2× restyle if they want.
    const canGenerate = selected.length === 3;

    const generate = async () => {
        if (!canGenerate) {
            toast.error("Selecteer eerst 3 foto's");
            return;
        }
        setGenerating(true);
        try {
            await api.post(
                `/admin/prospecting/runs/${item.run_id}/listings/${item.idx}/generate`,
                {
                    selections: selected.map((i) => ({
                        photo_index: i,
                        action: actions[i] || "relight",
                        note: (notes[i] || "").trim() || null,
                    })),
                }
            );
            // Fire-and-forget: backend now runs the 3 Fal.ai retouches in
            // parallel as a background task (Cloudflare's 100s edge timeout
            // killed the sync version). Close the modal — the auto-refresh
            // every 15s will surface the draft once the background work
            // completes (~30s typically).
            toast.success("Bewerking gestart. Draft verschijnt over ~30 sec in 'Te reviewen'.");
            if (onDone) onDone(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Genereren faalde");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="pending-listing-modal"
        >
            <div className="w-full max-w-5xl max-h-[92vh] flex flex-col bg-white border border-zinc-200 rounded-sm shadow-2xl">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 p-4 border-b border-zinc-200">
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E5A93D] mb-1 font-bold">Foto-selectie</p>
                        <h3 className="font-display text-lg font-bold truncate">{item.agency_name}</h3>
                        <p className="text-xs text-zinc-500 mt-0.5 truncate">
                            {item.listing_title} {item.city && `· ${item.city}`}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="pending-listing-modal-close"
                        className="shrink-0 h-9 w-9 grid place-items-center rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Photo grid */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4">
                    <p className="text-xs text-zinc-500 mb-3">
                        Tik op de foto&apos;s die u door AI wil laten bewerken (max 3). Daarna klikt u <strong>AI-suggestie</strong> om per foto een aanbevolen bewerking te krijgen.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="pending-listing-photo-grid">
                        {photos.map((url, i) => {
                            const isSel = selected.includes(i);
                            const order = isSel ? selected.indexOf(i) + 1 : null;
                            return (
                                <button
                                    type="button"
                                    key={i}
                                    onClick={() => togglePhoto(i)}
                                    data-testid={`pending-listing-photo-${i}`}
                                    data-selected={isSel ? "true" : "false"}
                                    className={`relative aspect-[4/3] rounded-sm overflow-hidden border-2 transition group ${
                                        isSel ? "border-[#E5A93D] ring-2 ring-[#E5A93D]/40" : "border-transparent hover:border-zinc-400"
                                    }`}
                                >
                                    <img src={url} alt={`foto ${i+1}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                    {isSel && (
                                        <span className="absolute top-1.5 left-1.5 inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-sm bg-[#E5A93D] text-black text-xs font-bold">
                                            {order}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Selection strip */}
                {selected.length > 0 && (
                    <div className="border-t border-white/10 p-4 bg-black/40">
                        <div className="flex items-center justify-between mb-3 gap-3">
                            <p className="text-xs text-zinc-500">
                                {selected.length}/3 geselecteerd — kies dezelfde of verschillende bewerkingen, jij beslist.
                            </p>
                            <button
                                type="button"
                                onClick={runVision}
                                disabled={scoring || generating || selected.length === 0}
                                data-testid="pending-listing-vision-btn"
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-[#E5A93D]/40 text-[#E5A93D] bg-[#E5A93D]/10 hover:bg-[#E5A93D]/20 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {scoring ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                                AI-suggestie ({selected.length} × €0.01)
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {selected.map((idx, order) => {
                                const url = photos[idx];
                                const score = item.photo_scores?.[String(idx)];
                                const reason = score?.reason;
                                return (
                                    <div key={idx} className="flex flex-col gap-2 border border-white/15 rounded-sm p-2 bg-black/40">
                                        <div className="flex items-center gap-3">
                                            <div className="relative w-16 h-16 shrink-0 rounded-sm overflow-hidden">
                                                {url && <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                                                <span className="absolute top-0.5 left-0.5 inline-flex items-center justify-center h-5 w-5 rounded-sm bg-[#E5A93D] text-black text-[10px] font-bold">
                                                    {order + 1}
                                                </span>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <select
                                                    value={actions[idx] || "relight"}
                                                    onChange={(e) => setActions((a) => ({ ...a, [idx]: e.target.value }))}
                                                    data-testid={`pending-listing-action-${idx}`}
                                                    className="w-full h-8 bg-white border border-zinc-200 rounded-sm px-2 text-xs text-zinc-800 focus:outline-none focus:border-[#E5A93D]/60"
                                                >
                                                    {ACTION_OPTIONS.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                                {reason && (
                                                    <p className="mt-1 text-[10px] text-zinc-500 line-clamp-2">{reason}</p>
                                                )}
                                            </div>
                                        </div>
                                        {/* Extra-note toggle + input — admin can add a free-text tweak
                                            on top of the preset (e.g. "vervang roze bank door grijs"). */}
                                        {!showNoteFor[idx] ? (
                                            <button
                                                type="button"
                                                onClick={() => setShowNoteFor((nf) => ({ ...nf, [idx]: true }))}
                                                data-testid={`pending-listing-note-toggle-${idx}`}
                                                className="self-start inline-flex items-center gap-1 h-6 px-2 rounded-sm border border-zinc-200 text-[10px] uppercase tracking-[0.1em] font-bold text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition"
                                            >
                                                + Extra opmerking voor AI
                                            </button>
                                        ) : (
                                            <div>
                                                <textarea
                                                    value={notes[idx] || ""}
                                                    onChange={(e) => setNotes((n) => ({ ...n, [idx]: e.target.value }))}
                                                    placeholder='Bv. "vervang het roze bankje door een grijs Scandinavisch model" of "haal alle kunstwerken van de muur"'
                                                    rows={2}
                                                    maxLength={400}
                                                    data-testid={`pending-listing-note-${idx}`}
                                                    className="w-full bg-white border border-[#E5A93D]/40 rounded-sm px-2 py-1.5 text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-[#E5A93D] resize-none"
                                                />
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-[10px] text-zinc-500">{(notes[idx] || "").length}/400</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowNoteFor((nf) => ({ ...nf, [idx]: false }));
                                                            setNotes((n) => { const c = { ...n }; delete c[idx]; return c; });
                                                        }}
                                                        data-testid={`pending-listing-note-remove-${idx}`}
                                                        className="text-[10px] text-zinc-500 hover:text-red-700"
                                                    >
                                                        verwijderen
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-zinc-200 bg-white">
                    <p className="text-[11px] text-zinc-500">
                        Kost ~€0.05 (3× Fal.ai retouch). Draft komt in &quot;Te reviewen&quot;.
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="pending-listing-cancel"
                            disabled={generating}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-xs disabled:opacity-50"
                        >
                            Annuleer
                        </button>
                        <button
                            type="button"
                            onClick={generate}
                            disabled={!canGenerate || generating || scoring}
                            data-testid="pending-listing-generate"
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Genereer draft (≈ €0.05)
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
